import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { renderJobOrderText } from '@lensmira/domain'

import { useTranslation } from '../i18n.js'
import { code128ModuleCount, code128Path } from '../barcode/code128.js'
import type { JobTicketData } from '../api/reads.js'

/**
 * The job ticket a lab is sent. §6.9 `JOB-011`, Appendix L, §15.3, `decisions/0015`.
 *
 * `V1-SPEC.md` §2 excludes lab EDI and VCA/OMA, so this is not a fallback in v1: it is the only way
 * a job reaches a lab. A job could be created, sent and collected with no way to tell the lab what
 * to make.
 *
 * **"Designed to be photographed on one phone and read on another"** is Appendix L's own wording and
 * it is the layout brief. So: large type throughout, a single column that does not reflow into
 * something else at a different width, the powers in a table with one row per eye, high contrast,
 * and the barcode drawn as an SVG at a module width that survives a camera. Nothing on it is
 * decorative, because every millimetre spent on decoration is a millimetre not spent on the
 * numbers somebody is squinting at.
 *
 * **The powers arrive as signed strings and are printed, not parsed.** The server formats them, per
 * `RX-03`'s "the sign is always rendered", and this screen has no arithmetic in it at all: a lab
 * document is the last place to recompute a value that was frozen at order time.
 *
 * **Western digits whatever the shop's language.** `LOC-005` puts dioptric values and numbers
 * outside the mirroring and the localisation, and the ticket body is wrapped `dir="ltr"` for the
 * same reason the Rx grid is: a prescription read right to left is a prescription with the eyes
 * swapped.
 *
 * **Three ways out, and the server sends none of them.** `decisions/0015`: copy the order text,
 * which is the primary path and has no length limit; a `mailto:` to the lab, which some clients
 * truncate above about a thousand characters, so it is offered second; and print, for a shop with
 * no email at all. The job moves to `sent` through the existing transition when staff confirm they
 * sent it, because nothing here can know whether a mail client actually did.
 */

export interface JobTicketProps {
  readonly ticket: JobTicketData
  /** Injectable so a test can assert the print was asked for without jsdom opening a dialog. */
  readonly print?: () => void
  /** Injectable for the same reason: jsdom has no clipboard. */
  readonly copy?: (text: string) => Promise<void>
}

/**
 * Everything on the page except the ticket, hidden for print by the shell's own rule.
 *
 * `.lm-screen > *:not(.lm-slip):not(.lm-job-ticket__sheet)` collapses the direct children of the
 * screen, which is where this screen's own buttons live. What it does not reach is anything
 * rendered **beside** this component by `App.tsx`: the Back and Close buttons, `JobDetail` itself
 * and the corrections panel are siblings of this `<section>`, not of the sheet, so each carries
 * `lm-no-print` at the call site instead. Asserted in `test/i18n-static.test.ts`.
 */

/** The module width the barcode is drawn at, in CSS pixels. */
const MODULE_PX = 2
const BARCODE_HEIGHT = 56
/** Code 128 asks for ten modules of quiet zone each side, and a camera needs them. */
const QUIET_MODULES = 10

export function JobTicket({ ticket, print, copy }: JobTicketProps): JSX.Element {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  /** The order text on screen, for when there is no clipboard API to put it on. */
  const [fallback, setFallback] = useState<string | null>(null)

  /**
   * Tell the stylesheet which paper this document wants, for as long as it is on screen.
   *
   * **Without it the shop gets a blank 58mm slip before the A4 ticket.** `@page` cannot be selected
   * on an ancestor condition, the default page in this stylesheet is the thermal roll
   * (`PK-024`), and a named page forces a break wherever the value changes, so naming the sheet
   * alone leaves the root on 58mm and produces two pages. Measured in Chromium: `164x567` points
   * then `595x842`.
   *
   * Cleaned up on unmount, so closing the ticket does not leave the receipt printing on A4.
   */
  useEffect(() => {
    const body = globalThis.document?.body
    body?.classList.add('lm-printing-ticket')
    return () => {
      body?.classList.remove('lm-printing-ticket')
    }
  }, [])

  const onPrint = useCallback(() => {
    if (print) {
      print()
      return
    }
    // Guarded because jsdom has no `print` and a test that opened one would hang. Same shape as
    // `Receipt.tsx`, which is the other printable document in the product.
    if (typeof window !== 'undefined' && typeof window.print === 'function') window.print()
  }, [print])

  const onCopy = useCallback(() => {
    const text = renderJobOrderText(ticket)
    const write =
      copy ??
      (async (value: string) => {
        /**
         * **A missing clipboard is a failure, and the first version called it a success.**
         * `await navigator?.clipboard?.writeText(v)` evaluates to `undefined` when the API is
         * absent, the await resolves, and the screen said "Copied" having copied nothing. The
         * counter runs over plain HTTP on a shop's own network, which is an insecure context and
         * exactly where `navigator.clipboard` is absent, so `decisions/0015`'s primary path would
         * have failed silently everywhere it is actually used.
         */
        const clipboard = globalThis.navigator?.clipboard
        if (typeof clipboard?.writeText !== 'function') {
          throw new Error('no clipboard')
        }
        await clipboard.writeText(value)
      })
    void write(text).then(
      () => {
        setCopied(true)
        setFallback(null)
      },
      () => {
        /**
         * The text itself, in a box the shop can select and copy by hand. An insecure context has
         * no clipboard API and the shop still has to get the order to their lab, so the fallback is
         * the thing they need rather than an apology.
         */
        setCopied(false)
        setFallback(text)
      },
    )
  }, [copy, ticket])

  const mailto =
    ticket.lab?.email === undefined
      ? undefined
      : `mailto:${encodeURIComponent(ticket.lab.email)}` +
        `?subject=${encodeURIComponent(`Job ${ticket.jobNumber}`)}` +
        `&body=${encodeURIComponent(renderJobOrderText(ticket))}`

  const totalModules = code128ModuleCount(ticket.barcodeValue)
  const width = (totalModules + QUIET_MODULES * 2) * MODULE_PX

  return (
    <section className="lm-screen" aria-labelledby="job-ticket-heading">
      <div className="lm-screen__actions lm-no-print">
        <button type="button" className="lm-button lm-button--primary" onClick={onCopy}>
          {t('jobTicket.copyOrder')}
        </button>
        {mailto === undefined ? null : (
          <a className="lm-button lm-button--secondary" href={mailto}>
            {t('jobTicket.emailLab')}
          </a>
        )}
        <button type="button" className="lm-button lm-button--secondary" onClick={onPrint}>
          {t('jobTicket.print')}
        </button>
      </div>
      {copied ? (
        <p className="lm-hint lm-no-print" role="status">
          {t('jobTicket.copied')}
        </p>
      ) : null}
      {fallback === null ? null : (
        <div className="lm-no-print">
          <p className="lm-hint" role="status">
            {t('jobTicket.copyByHand')}
          </p>
          <textarea
            className="lm-input lm-job-ticket__fallback"
            readOnly
            dir="ltr"
            rows={12}
            value={fallback}
            aria-label={t('jobTicket.orderText')}
            // Selected on mount, so copying it is one keystroke rather than a drag.
            ref={(node) => node?.select()}
          />
        </div>
      )}

      {/* `dir="ltr"` on the whole document: `LOC-005` keeps dioptric values and the prescription
          grid out of the mirroring, and a ticket read right to left is one with the eyes swapped. */}
      <article className="lm-job-ticket__sheet" dir="ltr">
        <h1 id="job-ticket-heading" className="lm-job-ticket__number">
          {ticket.jobNumber}
        </h1>
        {/* Appendix L: the Code 128 symbol plus the number in human-readable form beneath, which is
            the heading above. One path, so a photographed barcode has no seams between bars. */}
        <svg
          className="lm-job-ticket__barcode"
          width={width}
          height={BARCODE_HEIGHT}
          viewBox={`0 0 ${totalModules + QUIET_MODULES * 2} ${BARCODE_HEIGHT}`}
          role="img"
          aria-label={t('jobTicket.barcodeLabel', { number: ticket.jobNumber })}
        >
          <g transform={`translate(${QUIET_MODULES} 0)`}>
            <path d={code128Path(ticket.barcodeValue, BARCODE_HEIGHT)} fill="currentColor" />
          </g>
        </svg>

        <p className="lm-job-ticket__shop">
          {ticket.branchName === undefined
            ? ticket.shopName
            : `${ticket.shopName} (${ticket.branchName})`}
        </p>

        <dl className="lm-job-ticket__facts">
          <dt>{t('jobTicket.lab')}</dt>
          <dd>
            {ticket.lab === undefined ? t('jobTicket.noLab') : ticket.lab.name}
            {ticket.lab?.phone === undefined ? '' : ` ${ticket.lab.phone}`}
          </dd>
          <dt>{t('jobTicket.promised')}</dt>
          <dd>{ticket.promisedOn ?? t('jobTicket.noPromisedDate')}</dd>
        </dl>

        {ticket.frame.kind === 'own' ? (
          <section className="lm-job-ticket__block lm-job-ticket__block--warn">
            <h2 className="lm-job-ticket__blockHeading">{t('jobTicket.ownFrame')}</h2>
            <p className="lm-job-ticket__ownFrame">{ticket.frame.description}</p>
          </section>
        ) : ticket.frame.kind === 'ambiguous' ? (
          /* The order has more than one frame and nothing records which one this job is for
             (`later_todo.md` 17). Said plainly, because a lab that sees no frame section assumes
             the shop is sending one and waits for it. */
          <section className="lm-job-ticket__block lm-job-ticket__block--warn">
            <h2 className="lm-job-ticket__blockHeading">{t('jobTicket.frame')}</h2>
            {/* A number, not a string: `pick` throws `TypeError` on a non-numeric `count`,
                because `Intl.PluralRules.select` cannot choose a form from "2". */}
            <p>{t('jobTicket.frameAmbiguous', { count: ticket.frame.count })}</p>
          </section>
        ) : (
          <section className="lm-job-ticket__block">
            <h2 className="lm-job-ticket__blockHeading">{t('jobTicket.frame')}</h2>
            <p>{[ticket.frame.brand, ticket.frame.model].filter(Boolean).join(' ')}</p>
            <dl className="lm-job-ticket__facts">
              <dt>{t('jobTicket.colour')}</dt>
              <dd>{ticket.frame.colourCode ?? t('jobTicket.notGiven')}</dd>
              <dt>{t('jobTicket.size')}</dt>
              <dd>
                {[ticket.frame.eyeSizeMm, ticket.frame.bridgeMm, ticket.frame.templeMm]
                  .filter((part): part is number => part !== undefined)
                  .join('-') || t('jobTicket.notGiven')}
              </dd>
              {ticket.frame.mountingNotes === undefined ? null : (
                <>
                  <dt>{t('jobTicket.mounting')}</dt>
                  <dd>{ticket.frame.mountingNotes}</dd>
                </>
              )}
            </dl>
          </section>
        )}

        <section className="lm-job-ticket__block">
          <h2 className="lm-job-ticket__blockHeading">{t('jobTicket.prescription')}</h2>
          <table className="lm-job-ticket__rx">
            <thead>
              <tr>
                <th scope="col">{t('jobTicket.eye')}</th>
                <th scope="col">{t('jobTicket.sphere')}</th>
                <th scope="col">{t('jobTicket.cylinder')}</th>
                <th scope="col">{t('jobTicket.axis')}</th>
                <th scope="col">{t('jobTicket.add')}</th>
                <th scope="col">{t('jobTicket.pd')}</th>
                <th scope="col">{t('jobTicket.segHeight')}</th>
              </tr>
            </thead>
            <tbody>
              {ticket.eyes.map((eye) => (
                <tr key={eye.eye}>
                  <th scope="row">{eye.eye.toUpperCase()}</th>
                  {/* Printed, never parsed. The server signed and rounded these. */}
                  <td>{eye.sphere}</td>
                  <td>{eye.cylinder}</td>
                  <td>{eye.axis ?? ''}</td>
                  <td>{eye.addPower ?? ''}</td>
                  <td>{eye.monocularPdMm ?? ''}</td>
                  <td>{eye.segmentHeightMm ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {ticket.eyes.map((eye) =>
            eye.prismHorizontal === undefined && eye.prismVertical === undefined ? null : (
              <p key={`prism-${eye.eye}`} className="lm-job-ticket__prism">
                {/* A prism with no base is the wrong direction, so the base travels with it. */}
                {eye.eye.toUpperCase()}
                {eye.prismHorizontal === undefined
                  ? ''
                  : ` ${eye.prismHorizontal} ${eye.prismBaseHorizontal ?? ''}`}
                {eye.prismVertical === undefined
                  ? ''
                  : ` ${eye.prismVertical} ${eye.prismBaseVertical ?? ''}`}
              </p>
            ),
          )}
        </section>

        <section className="lm-job-ticket__block">
          <h2 className="lm-job-ticket__blockHeading">{t('jobTicket.lens')}</h2>
          {ticket.eyes.map((eye) => (
            <dl key={`lens-${eye.eye}`} className="lm-job-ticket__facts">
              <dt>{eye.eye.toUpperCase()}</dt>
              <dd>{eye.lensProductName ?? t('jobTicket.notGiven')}</dd>
              <dt>{t('jobTicket.treatments')}</dt>
              <dd>
                {eye.treatmentNames === undefined || eye.treatmentNames.length === 0
                  ? t('jobTicket.notGiven')
                  : eye.treatmentNames.join(', ')}
              </dd>
              {eye.diameterMm === undefined ? null : (
                <>
                  <dt>{t('jobTicket.diameter')}</dt>
                  <dd>{eye.diameterMm}</dd>
                </>
              )}
              {eye.baseCurve === undefined ? null : (
                <>
                  <dt>{t('jobTicket.baseCurve')}</dt>
                  <dd>{eye.baseCurve}</dd>
                </>
              )}
            </dl>
          ))}
        </section>

        {ticket.specialInstructions === undefined ? null : (
          <section className="lm-job-ticket__block lm-job-ticket__block--warn">
            <h2 className="lm-job-ticket__blockHeading">{t('jobTicket.specialInstructions')}</h2>
            <p>{ticket.specialInstructions}</p>
          </section>
        )}

        {ticket.absent.length === 0 ? null : (
          /* On the sheet and not only on screen. Appendix L's mandatory content that this job
             cannot supply, said to the lab so they ask rather than making the wrong thing. */
          <section className="lm-job-ticket__block lm-job-ticket__block--warn">
            <h2 className="lm-job-ticket__blockHeading">{t('jobTicket.notSupplied')}</h2>
            <ul className="lm-job-ticket__absent">
              {ticket.absent.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>
        )}
      </article>
    </section>
  )
}
